import React from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  FormCaption,
  InformSpan,
  FormSubmit,
  Input,
  ArrowLink
} from '../../common/components';
import {
  AUTH_PATTERN,
  PasswordErrors,
  AUTH_VALIDATION_CONFIG
} from './validation.pattern';
import { ROUTER_LINKS } from '../../common/consts/app-keys.const';
import { authService } from '../service/auth.service';
import { IResetPassword } from '../types';
import { APP_KEYS } from '../../common/consts';
import { useThrottle } from '../../hooks/useThrottle';
import { LockInputIcon } from '../../common/components/icons/lock-input-icon.component';

type Inputs = {
  password: string;
  confirmPassword: string;
};

export const CreateNewPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useParams<{ token: string }>();
  const email = (location.state as { email?: string })?.email;
  const {
    register,
    watch,
    handleSubmit,
    setError,
    formState: { errors }
  } = useForm<Inputs>();

  const { mutate: resetPassword, isPending: resetPasswordPending } = useMutation({
    mutationKey: [APP_KEYS.QUERY_KEYS.USER],
    mutationFn: (data: IResetPassword) => authService.resetPassword(data),
    onSuccess: () => {
      navigate(ROUTER_LINKS.PASSWORD_UPDATED);
    },
    onError: (err: any) => {
      if (
        err.response.data.message === 'Password cannot be the same as the old one'
      ) {
        setError('password', {
          message: PasswordErrors.same,
          type: 'manual'
        });
      } else if (err.response.data.message === 'Invalid Password') {
        setError('password', {
          message: PasswordErrors.wrong,
          type: 'manual'
        });
      }
    }
  });
  const throttledResetPassword = useThrottle(resetPassword);

  const onSubmit: SubmitHandler<Inputs> = (dataInputs: Inputs) => {
    if (!email || !token) return;

    throttledResetPassword({
      email,
      password: dataInputs.password,
      token
    });
  };

  return (
    <>
      <FormCaption>{t('auth.newPasswordTitle')}</FormCaption>
      <InformSpan>{t('auth.newPasswordDifferent')}</InformSpan>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col">
        <Input
          errors={errors}
          name="password"
          label={t('auth.newPasswordLabel')}
          placeholder={t('auth.enterHere')}
          icon={<LockInputIcon />}
          type="password"
          register={register.bind(null, 'password', {
            required: { message: PasswordErrors.required, value: true },
            pattern: {
              value: AUTH_PATTERN.password,
              message: PasswordErrors.pattern
            },
            minLength: {
              value: AUTH_VALIDATION_CONFIG.password.minLength,
              message: PasswordErrors.minLength
            },
            maxLength: {
              value: AUTH_VALIDATION_CONFIG.password.maxLength,
              message: PasswordErrors.maxLength
            }
          })}
        />
        <Input
          errors={errors}
          name="confirmPassword"
          label={t('auth.confirmPasswordLabel')}
          placeholder={t('auth.enterHere')}
          type="password"
          icon={<LockInputIcon />}
          register={register.bind(null, 'confirmPassword', {
            required: { message: PasswordErrors.required, value: true },
            pattern: {
              value: AUTH_PATTERN.password,
              message: PasswordErrors.pattern
            },
            minLength: {
              value: AUTH_VALIDATION_CONFIG.password.minLength,
              message: PasswordErrors.minLength
            },
            maxLength: {
              value: AUTH_VALIDATION_CONFIG.password.maxLength,
              message: PasswordErrors.maxLength
            },
            validate: (val: string) => {
              if (watch('password') !== val) {
                return t('auth.passwordsNoMatch');
              }
            }
          })}
        />

        <FormSubmit
          className="md:mt-[2px] mb-[24px] md:mb-[24px]"
          isLoading={resetPasswordPending}
          value={t('auth.savePassword')}
        />
        <ArrowLink to={ROUTER_LINKS.SIGNIN} linkText={t('auth.backToLogin')} />
      </form>
    </>
  );
};
